import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs';

import { LoadingService } from '../services/loading.service';
import { SKIP_GLOBAL_LOADING } from '../utils/silent-request.util';

/**
 * Drives the global full-screen loading overlay off HTTP activity.
 *
 * Every request registers a loading holder on the {@link LoadingService} when it
 * starts and releases it when it settles (success or error). Because the service
 * counts holders, the overlay stays up until *all* concurrent requests of a page
 * have finished — which is exactly what we want when a view fans out to several
 * endpoints at once.
 *
 * Static asset requests (translation JSON under /assets/) are skipped so the
 * overlay doesn't flash during boot or language switches.
 *
 * A request can also opt out explicitly with the SKIP_GLOBAL_LOADING context
 * token. That is for background work the reader is not waiting on — an
 * optimistic like has already drawn its result, and blocking the application
 * until the server confirms would throw that away.
 */
const SKIP_URL_FRAGMENTS: ReadonlyArray<string> = ['/assets/'];

export const loadingInterceptor: HttpInterceptorFn = (request, next) => {
  const shouldSkip =
    request.context.get(SKIP_GLOBAL_LOADING) ||
    SKIP_URL_FRAGMENTS.some((fragment) => request.url.includes(fragment));

  if (shouldSkip) {
    return next(request);
  }

  const loadingService = inject(LoadingService);
  loadingService.show();

  return next(request).pipe(finalize(() => loadingService.hide()));
};
